import React from 'react';
import { Box, Typography, Button, Container, Paper, Avatar } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Login as LoginIcon,
  PersonAdd as SignupIcon,
  Lock as LockIcon
} from '@mui/icons-material';

/**
 * Composant de fallback pour les pages non authentifiées
 * Affiche des options de connexion et d'inscription
 */
const UnauthenticatedFallback = ({
  title,
  message,
  showLoginButton = true,
  showSignupButton = true,
  actionUrl
}) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['common']);

  const handleLoginClick = () => {
    navigate('/login');
  };

  const handleSignupClick = () => {
    navigate('/register');
  };

  const getContentLength = () => {
    const lang = i18n?.language || navigator.language || 'en';
    if (['ar', 'he', 'fa'].includes(lang)) return 'rtl';
    return 'ltr';
  };

  return (
    <Container maxWidth="sm" sx={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '100vh',
      padding: '20px'
    }}>
      <Paper
        elevation={3}
        sx={{
          padding: '40px 30px',
          borderRadius: '16px',
          textAlign: 'center',
          backgroundColor: '#ffffff',
          boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
        }}
      >
        {/* Icône */}
        <Avatar
          sx={{
            width: 80,
            height: 80,
            margin: '0 auto 24px',
            backgroundColor: '#e3f2fd',
            color: '#1976d2',
          }}
        >
          <LockIcon sx={{ fontSize: 40 }} />
        </Avatar>

        {/* Titre */}
        <Typography
          variant="h5"
          component="h1"
          sx={{
            marginBottom: '12px',
            fontWeight: 600,
            color: '#1a1a1a',
          }}
        >
          {title || t('auth.unauthenticated_title', 'Authentification requise')}
        </Typography>

        {/* Message */}
        <Typography
          variant="body1"
          sx={{
            marginBottom: '32px',
            color: '#666666',
            lineHeight: 1.6,
          }}
        >
          {message || t('auth.unauthenticated_message', 'Veuillez vous connecter ou créer un compte pour accéder à cette page.')}
        </Typography>

        {/* Boutons d'action */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            direction: getContentLength(),
          }}
        >
          {showLoginButton && (
            <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleLoginClick}
                sx={{
                    padding: '12px 24px',
                    fontSize: '16px',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                }}
                >
                <LoginIcon />
                {t('auth.login.title', 'Se connecter')}
                </Button>
          )}

          {showSignupButton && (
            <Button
              variant="outlined"
              color="primary"
              size="large"
              onClick={handleSignupClick}
              sx={{
                    padding: '12px 24px',
                    fontSize: '16px',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                }}
            >
              <SignupIcon />
              {t('auth.signup', 'Créer un compte')}
            </Button>
          )}
        </Box>

        {/* Info supplémentaire */}
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            marginTop: '24px',
            color: '#999999',
          }}
        >
          {t('auth.already_have_account', 'Vous avez déjà un compte ?')} {' '}
          <Typography
            component="span"
            variant="caption"
            sx={{ color: '#1976d2', fontWeight: 600, cursor: 'pointer' }}
            onClick={handleLoginClick}
          >
            {t('auth.login_here', 'Connectez-vous ici')}
          </Typography>
        </Typography>
      </Paper>
    </Container>
  );
};

export default UnauthenticatedFallback;
